import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundAccess, { fundPeopleUrl, myFundAccessUrl } from '@/hooks/useFundAccess';
import { getAuth, postAuth, putAuth } from '@/api/ApiCalls';
import CustomMenu from '@/components/ui/Menu';
import crownIcon from '@/assets/icons/crown.svg';
import editIcon from '@/assets/icons/edit.svg';
import xCircleIcon from '@/assets/icons/x-circle.svg';
import userIcon from '@/assets/icons/user.svg';
import leaveIcon from '@/assets/icons/leave.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { AddFundRepresentativeDtoIn } from '@/models/in/AddFundRepresentativeDtoIn';
import { FundMyAccessDtoOut, FundPersonDtoOut, WorldLevels } from '@/models/out/FundPersonDtoOut';
import { CoordinatorAddRequestDtoOut, fundAddRequestsUrl } from '@/models/out/CoordinatorAddRequestDtoOut';
import { describeWorlds, summaryLevel, worldsOf, worldsSummary } from '@/helpers/permissionWorlds';
import SettingsHistorySection from '../sections/SettingsHistorySection';
import { AddCoordinatorDialog, HandoffDialog, IsraeliIdDialog, PermissionChip, PermissionsExplainerDialog, WorldsDialog } from './PeopleDialogs';
import { BellIcon, CrownIcon, EyeIcon, IdCardIcon, InfoIcon, LockIcon } from './peopleIcons';
import settingsStyles from '../FundSettings.module.css';
import styles from './People.module.css';

type ConfirmKind = 'remove' | 'makeFounder' | 'stepDown' | 'leave' | 'requestFull';

type DialogState =
  | null
  | { kind: 'add' }
  | { kind: 'israeliId' }
  | { kind: 'handoff' }
  | { kind: 'explainer' }
  | { kind: 'worlds'; person: FundPersonDtoOut }
  | { kind: 'confirm'; action: ConfirmKind; person?: FundPersonDtoOut };

/** The new endpoints answer in Hebrew, ready for the screen; anything else gets the generic sentence. */
const errorText = (err: unknown, fallback: string) =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data.trim() ? err.response.data : fallback;

const fullName = (p: { firstName: string | null; lastName: string | null }) => `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();

interface PeopleSectionProps {
  fundId: number;
  fundName: string;
}

/**
 * «אנשי הפרויקט» run by the fund's founders (15.10.2026). Everyone with access is a coordinator
 * with a role and a permission; a founder manages the list - adds and removes coordinators, sets
 * permissions, appoints founders, hands the role over - and removes only themselves. A founder
 * appointed without an Israeli ID is shown as one, with the role waiting for the number, which
 * they add right here. The office sees the same list read-only in the management area.
 */
const PeopleSection = ({ fundId, fundName }: PeopleSectionProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const peopleUrl = fundPeopleUrl(fundId);
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data: people, isLoading, isError } = useGetAuthApi<FundPersonDtoOut[]>(peopleUrl);
  const { access } = useFundAccess(fundId);

  const [dialog, setDialog] = useState<DialogState>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const me = people?.find((p) => p.isCurrentUser);
  const canManage = !!access?.canManagePeople;
  const activeFounders = (people ?? []).filter((p) => p.role === 'Founder' && p.founderStatus === 'Active');
  const requesters = canManage ? (people ?? []).filter((p) => p.hasPendingPermissionRequest) : [];
  // A lead coordinator asks the office to add a coordinator (SPR-10486); the office, here too, adds.
  const addAsRequest = !access?.isOffice;
  const addRequestsUrl = canManage ? fundAddRequestsUrl(fundId) : '';
  const { data: addRequests } = useGetAuthApi<CoordinatorAddRequestDtoOut[]>(addRequestsUrl);
  const myStatus = access?.founderStatus;
  // Short of full permission somewhere: view only, or a mix of worlds (Druk, 22.10.2026).
  const myWorlds = access ? worldsOf(access) : null;
  const amShortOfFull = !!access && !access.isOffice && access.role !== 'Founder' && myWorlds != null && worldsSummary(myWorlds) !== 'full';
  const amPendingFounder = access?.role === 'Founder' && myStatus !== 'Active' && myStatus != null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [peopleUrl] });
    queryClient.invalidateQueries({ queryKey: [myFundAccessUrl(fundId)] });
    queryClient.invalidateQueries({ queryKey: [`funds/financial-data/${fundId}/settings-history`] });
    queryClient.invalidateQueries({ queryKey: [`funds/financial-data/${fundId}/coordinators`] });
    queryClient.invalidateQueries({ queryKey: [fundAddRequestsUrl(fundId)] });
  };

  const close = () => {
    setDialog(null);
    setDialogError(null);
  };

  const action = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: object }) => postAuth<unknown, object>(path ? `${peopleUrl}/${path}` : peopleUrl, headers, body ?? {}),
    onSuccess: () => refresh(),
  });

  const run = (path: string, body?: object, after?: () => void) => {
    setDialogError(null);
    setNotice(null);
    action.mutate(
      { path, body },
      {
        onSuccess: () => {
          close();
          after?.();
        },
        onError: (err) => {
          const text = errorText(err, t('fundPeople.genericError'));
          if (dialog) setDialogError(text);
          else setNotice(text);
        },
      }
    );
  };

  /*
   * The ID goes through «החשבון שלי»'s own endpoint: the number belongs to the account, not the
   * fund. Saving it turns a waiting founder into one in effect on the server; the screen re-reads
   * the fund and says which way it went.
   */
  const saveIsraeliId = useMutation({
    mutationFn: (israeliId: string) => putAuth<unknown, object, { israeliId: string }>('users/israeli-id', headers, {}, { israeliId }),
  });

  const submitIsraeliId = (israeliId: string) => {
    setDialogError(null);
    saveIsraeliId.mutate(israeliId, {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['users/get'] });
        refresh();
        const updated = await queryClient.fetchQuery({
          queryKey: [myFundAccessUrl(fundId), 'after-id'],
          queryFn: () => getAuth<FundMyAccessDtoOut>(myFundAccessUrl(fundId), headers),
        });
        close();
        setNotice(updated?.founderStatus === 'Active' ? t('fundPeople.idDialog.activated') : t('fundPeople.idDialog.savedButBlocked'));
      },
      onError: (err) => {
        if (axios.isAxiosError(err) && err.response?.status === 409) setDialogError(errorText(err, t('fundPeople.idDialog.taken')));
        else if (axios.isAxiosError(err) && err.response?.status === 403) setDialogError(t('fundPeople.idDialog.alreadySet'));
        else setDialogError(errorText(err, t('fundPeople.genericError')));
      },
    });
  };

  const openConfirm = (actionKind: ConfirmKind, person?: FundPersonDtoOut) => {
    setDialogError(null);
    setDialog({ kind: 'confirm', action: actionKind, person });
  };

  const menuItemsFor = (person: FundPersonDtoOut) => {
    if (person.isCurrentUser) {
      if (person.role === 'Founder' && person.founderStatus === 'Active') {
        const anotherFounder = activeFounders.length > 1;
        return [
          person.handingOverToName
            ? { icon: xCircleIcon, text: t('fundPeople.menu.cancelHandoff'), onClick: () => run('cancel-handoff') }
            : { icon: crownIcon, text: t('fundPeople.menu.handoff'), onClick: () => setDialog({ kind: 'handoff' }) },
          {
            icon: userIcon,
            text: t('fundPeople.menu.stepDown'),
            onClick: () => openConfirm('stepDown'),
            disabled: !anotherFounder,
            hint: anotherFounder ? undefined : t('fundPeople.menu.stepDownHint'),
          },
          {
            icon: leaveIcon,
            text: t('fundPeople.menu.leave'),
            onClick: () => openConfirm('leave'),
            danger: anotherFounder,
            disabled: !anotherFounder,
            hint: anotherFounder ? undefined : t('fundPeople.menu.leaveHint'),
          },
        ];
      }
      return [{ icon: leaveIcon, text: t('fundPeople.menu.leave'), onClick: () => openConfirm('leave'), danger: true }];
    }
    // A founder removes only themselves: another founder's row carries no actions at all.
    if (!canManage || person.role === 'Founder') return [];
    return [
      {
        icon: editIcon,
        text: t('fundPeople.menu.permissions'),
        onClick: () => {
          setDialogError(null);
          setDialog({ kind: 'worlds', person });
        },
      },
      { icon: crownIcon, text: t('fundPeople.menu.makeFounder'), onClick: () => openConfirm('makeFounder', person) },
      { icon: deleteIcon, text: t('fundPeople.menu.remove'), onClick: () => openConfirm('remove', person), danger: true },
    ];
  };

  const roleChip = (person: FundPersonDtoOut) => {
    if (person.role !== 'Founder') return <span className={`${styles.chip} ${styles.chipCoordinator}`}>{t('fundPeople.roleCoordinator')}</span>;
    const pending = person.founderStatus !== 'Active';
    return (
      <span className={`${styles.chip} ${pending ? styles.chipFounderPending : styles.chipFounder}`}>
        <CrownIcon />
        {t('fundPeople.roleFounder')}
      </span>
    );
  };

  const permissionChip = (person: FundPersonDtoOut) => <PermissionChip person={person} />;

  const submitWorlds = (person: FundPersonDtoOut, worlds: WorldLevels) => run('permission', { key: person.key, permissionLevel: summaryLevel(worlds), worlds });

  /** What to say under a person's name, if anything. */
  const subNote = (person: FundPersonDtoOut) => {
    if (person.role === 'Founder' && person.founderStatus !== 'Active') {
      if (person.founderBlockedReason) return <span className={`${styles.sub} ${styles.subAttention}`}>{t('fundPeople.blockedNote')}</span>;
      if (person.isCurrentUser) return <span className={`${styles.sub} ${styles.subAttention}`}>{t('fundPeople.pendingSelfNote')}</span>;
      if (person.founderStatus === 'PendingHandoff' && person.replacesFounderName)
        return <span className={styles.sub}>{t('fundPeople.pendingHandoffNote', { name: person.replacesFounderName })}</span>;
      return <span className={styles.sub}>{t('fundPeople.pendingOtherNote')}</span>;
    }
    if (person.handingOverToName) return <span className={styles.sub}>{t('fundPeople.handingOverNote', { name: person.handingOverToName })}</span>;
    if (!person.email) return <span className={styles.sub}>{t('fundPeople.noEmailNote')}</span>;
    if (!person.hasPortalAccess) return <span className={styles.sub}>{t('fundPeople.noPortalNote')}</span>;
    if (canManage && person.hasPendingPermissionRequest) return <span className={styles.sub}>{t('fundPeople.requestedNote')}</span>;
    return null;
  };

  const addIdButton = (person: FundPersonDtoOut) =>
    person.isCurrentUser && person.role === 'Founder' && person.founderStatus !== 'Active' && !person.founderBlockedReason ? (
      <button type="button" className={`${styles.pillButton} ${styles.pillGhost} ${styles.inlineIdButton}`} onClick={() => setDialog({ kind: 'israeliId' })}>
        <IdCardIcon />
        {t('fundPeople.addIdButton')}
      </button>
    ) : null;

  const nameCell = (person: FundPersonDtoOut) => (
    <>
      <span className={styles.name}>{fullName(person) || t('fundPeople.unnamed')}</span>
      {person.isCurrentUser && <span className={settingsStyles.mePill}>{t('fundPeople.you')}</span>}
      {subNote(person)}
    </>
  );

  const contactCell = (person: FundPersonDtoOut) => (
    <>
      {person.email && <bdi dir="ltr">{person.email}</bdi>}
      {person.phone && (
        <span className={styles.sub}>
          <bdi dir="ltr">{person.phone}</bdi>
        </span>
      )}
    </>
  );

  const menuFor = (person: FundPersonDtoOut) => {
    const items = menuItemsFor(person);
    return items.length > 0 ? <CustomMenu items={items} /> : null;
  };

  const founderNames = activeFounders.map(fullName).filter(Boolean).join(', ');
  const confirm = dialog?.kind === 'confirm' ? dialog : null;
  const confirmPerson = confirm?.person;
  const confirmCopy: Record<ConfirmKind, { title: string; message: string; confirmText: string; danger?: boolean; onConfirm: () => void }> = {
    remove: {
      title: t('fundPeople.confirm.removeTitle'),
      message: t('fundPeople.confirm.removeMessage', { name: confirmPerson ? fullName(confirmPerson) : '' }),
      confirmText: 'fundPeople.confirm.removeConfirm',
      danger: true,
      onConfirm: () => confirmPerson && run('remove', { key: confirmPerson.key }),
    },
    makeFounder: {
      title: t('fundPeople.confirm.makeFounderTitle'),
      message: t('fundPeople.confirm.makeFounderMessage', { name: confirmPerson ? fullName(confirmPerson) : '' }),
      confirmText: 'fundPeople.confirm.makeFounderConfirm',
      onConfirm: () => confirmPerson && run('make-founder', { key: confirmPerson.key }),
    },
    stepDown: {
      title: t('fundPeople.confirm.stepDownTitle'),
      message: t('fundPeople.confirm.stepDownMessage', {
        founders: activeFounders
          .filter((f) => !f.isCurrentUser)
          .map(fullName)
          .join(', '),
      }),
      confirmText: 'fundPeople.confirm.stepDownConfirm',
      onConfirm: () => run('step-down'),
    },
    leave: {
      title: t('fundPeople.confirm.leaveTitle'),
      message: t('fundPeople.confirm.leaveMessage'),
      confirmText: 'fundPeople.confirm.leaveConfirm',
      danger: true,
      // Gone from the fund: the next screen is the fund picker.
      onConfirm: () => run('leave', undefined, () => window.location.assign(`${import.meta.env.BASE_URL ?? '/'}`)),
    },
    requestFull: {
      title: t('fundPeople.confirm.requestTitle'),
      message: t('fundPeople.confirm.requestMessage'),
      confirmText: 'fundPeople.confirm.requestConfirm',
      onConfirm: () => run('request-full-permission'),
    },
  };
  const confirmContent = confirm ? confirmCopy[confirm.action] : null;

  return (
    <div className={settingsStyles.panel}>
      <div className={settingsStyles.sectionHeaderRow}>
        <h2 className={settingsStyles.panelTitle}>{t('fundPeople.title')}</h2>
        <div className={styles.headerActions}>
          <button type="button" className={styles.explainLink} onClick={() => setDialog({ kind: 'explainer' })}>
            <InfoIcon />
            {t('fundPeople.explainLink')}
          </button>
          {canManage ? (
            <button
              type="button"
              className={settingsStyles.editIconBtn}
              onClick={() => {
                setDialogError(null);
                setDialog({ kind: 'add' });
              }}
            >
              {t('fundPeople.addButton')}
            </button>
          ) : (
            amPendingFounder && (
              <span className={styles.addDisabled} title={t('fundPeople.addDisabledHint')} aria-disabled="true">
                <LockIcon />
                {t('fundPeople.addButton')}
              </span>
            )
          )}
        </div>
      </div>
      <p className={settingsStyles.panelSubtitle}>{t('fundPeople.intro')}</p>

      {notice && (
        <p className={settingsStyles.statusNote} role="status">
          {notice}
        </p>
      )}

      {amPendingFounder && (
        <div className={styles.notice}>
          <IdCardIcon />
          <span className={styles.noticeText}>
            {access?.founderBlockedReason ? (
              <>
                <b>{t('fundPeople.banner.blockedTitle')}</b> {t('fundPeople.banner.blockedText')}
              </>
            ) : myStatus === 'PendingHandoff' ? (
              <>
                <b>{t('fundPeople.banner.handoffTitle', { name: me?.replacesFounderName ?? '' })}</b> {t('fundPeople.banner.handoffText')}
              </>
            ) : (
              <>
                <b>{t('fundPeople.banner.pendingTitle')}</b> {t('fundPeople.banner.pendingText')}
              </>
            )}
          </span>
          {!access?.founderBlockedReason && (
            <button type="button" className={`${styles.pillButton} ${styles.pillPrimary}`} onClick={() => setDialog({ kind: 'israeliId' })}>
              {t('fundPeople.addIdButton')}
            </button>
          )}
        </div>
      )}

      {requesters.map((person) => (
        <div className={styles.notice} key={`request-${person.key}`}>
          <BellIcon />
          <span className={styles.noticeText}>{t('fundPeople.requests.line', { name: fullName(person) })}</span>
          <span className={styles.noticeButtons}>
            <button
              type="button"
              className={`${styles.pillButton} ${styles.pillPrimary}`}
              disabled={action.isPending}
              onClick={() => run('permission', { key: person.key, permissionLevel: 'Full' })}
            >
              {t('fundPeople.requests.approve')}
            </button>
            <button
              type="button"
              className={`${styles.pillButton} ${styles.pillGhost}`}
              disabled={action.isPending}
              onClick={() => run('reject-permission-request', { key: person.key })}
            >
              {t('fundPeople.requests.reject')}
            </button>
          </span>
        </div>
      ))}

      {(addRequests ?? []).map((request) => (
        <div className={styles.notice} key={`add-request-${request.id}`}>
          <BellIcon />
          <span className={styles.noticeText}>{t('fundPeople.addRequests.line', { name: fullName(request), email: request.email })}</span>
          <span className={styles.noticeButtons}>
            <button
              type="button"
              className={`${styles.pillButton} ${styles.pillGhost}`}
              disabled={action.isPending}
              onClick={() => run(`add-requests/${request.id}/cancel`, {}, () => setNotice(t('fundPeople.addRequests.cancelled')))}
            >
              {t('fundPeople.addRequests.cancel')}
            </button>
          </span>
        </div>
      ))}

      {amShortOfFull && myWorlds && (
        <div className={styles.notice}>
          <EyeIcon />
          <span className={styles.noticeText}>
            {worldsSummary(myWorlds) === 'viewOnly'
              ? t('fundPeople.viewOnly.line')
              : t('fundPeople.viewOnly.customLine', { worlds: describeWorlds(myWorlds, t) })}{' '}
            {access.hasPendingPermissionRequest && (activeFounders.length > 0 ? t('fundPeople.viewOnly.sent') : t('fundPeople.viewOnly.sentWaiting'))}
          </span>
          {!access.hasPendingPermissionRequest && (
            <button type="button" className={`${styles.pillButton} ${styles.pillPrimary}`} onClick={() => openConfirm('requestFull')}>
              {t('fundPeople.viewOnly.ask')}
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <p className={settingsStyles.panelSubtitle}>{t('loading')}</p>
      ) : isError ? (
        <p className={settingsStyles.formError}>{t('fundPeople.loadError')}</p>
      ) : !people || people.length === 0 ? (
        <p className={settingsStyles.empty}>{t('fundPeople.empty')}</p>
      ) : (
        <>
          <div className={`${settingsStyles.tableWrap} ${styles.tableView}`}>
            <table className={settingsStyles.table}>
              <thead>
                <tr>
                  <th>{t('fundPeople.columnName')}</th>
                  <th>{t('fundPeople.columnContact')}</th>
                  <th>{t('fundPeople.columnRole')}</th>
                  <th>{t('fundPeople.columnPermission')}</th>
                  <th className={styles.menuCell}>
                    <span className="sr-only">{t('fundPeople.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((person) => (
                  <tr key={person.key}>
                    <td>{nameCell(person)}</td>
                    <td>{contactCell(person)}</td>
                    <td>
                      {roleChip(person)}
                      {addIdButton(person) && <div>{addIdButton(person)}</div>}
                    </td>
                    <td>{permissionChip(person)}</td>
                    <td className={styles.menuCell}>{menuFor(person)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.cards}>
            {people.map((person) => (
              <div className={styles.card} key={`card-${person.key}`}>
                <div className={styles.cardHead}>
                  <span className={styles.name}>{fullName(person) || t('fundPeople.unnamed')}</span>
                  {person.isCurrentUser && <span className={settingsStyles.mePill}>{t('fundPeople.you')}</span>}
                  {roleChip(person)}
                  {permissionChip(person)}
                </div>
                <div className={styles.cardMenu}>{menuFor(person)}</div>
                <div className={styles.cardContact}>
                  {contactCell(person)}
                  {subNote(person)}
                  {addIdButton(person)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!isLoading && (
        <div className={settingsStyles.panelFooter}>
          {!canManage && (
            <span className={settingsStyles.panelSubtitle}>
              {access?.isOffice
                ? t('fundPeople.officeHint')
                : activeFounders.length > 0
                  ? t('fundPeople.nonFounderHint', { founders: founderNames })
                  : t('fundPeople.noActiveFounderHint')}
            </span>
          )}
          <SettingsHistorySection fundId={fundId} />
        </div>
      )}

      <AddCoordinatorDialog
        open={dialog?.kind === 'add'}
        onClose={close}
        isPending={action.isPending}
        error={dialogError}
        asRequest={addAsRequest}
        onSubmit={(data: AddFundRepresentativeDtoIn) =>
          addAsRequest ? run('add-requests', data, () => setNotice(t('fundPeople.addRequests.sent', { name: fullName(data) }))) : run('', data)
        }
      />
      <IsraeliIdDialog
        open={dialog?.kind === 'israeliId'}
        onClose={close}
        fundLabel={`${fundId} · ${fundName}`}
        isPending={saveIsraeliId.isPending}
        error={dialogError}
        onSubmit={submitIsraeliId}
      />
      <HandoffDialog
        open={dialog?.kind === 'handoff'}
        onClose={close}
        candidates={(people ?? []).filter((p) => !p.isCurrentUser && p.role !== 'Founder' && p.hasPortalAccess)}
        isPending={action.isPending}
        error={dialogError}
        onSubmit={(key) => run('handoff', { key })}
      />
      <PermissionsExplainerDialog open={dialog?.kind === 'explainer'} onClose={close} />
      <WorldsDialog
        open={dialog?.kind === 'worlds'}
        onClose={close}
        person={dialog?.kind === 'worlds' ? dialog.person : null}
        isPending={action.isPending}
        error={dialogError}
        onSubmit={(worlds) => dialog?.kind === 'worlds' && submitWorlds(dialog.person, worlds)}
      />

      {confirmContent && (
        <ConfirmationDialog
          open
          onOpenChange={(open) => !open && close()}
          title={confirmContent.title}
          message={confirmContent.message}
          confirmText={confirmContent.confirmText}
          variant={confirmContent.danger ? 'danger' : 'default'}
          isLoading={action.isPending}
          error={dialogError}
          onConfirm={confirmContent.onConfirm}
          onCancel={close}
        />
      )}
    </div>
  );
};

export default PeopleSection;
